import React, { useEffect, useState } from 'react';
import {
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	Box,
	Divider,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { groupBy } from 'lodash';
import { getTranslation } from 'common/modules/translations';
import { getPricingString } from 'common/utils/common';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import { useTranslation } from 'services/localization';
import useLocalFormat from 'hooks/useShopFormat';
import ContainerBox from 'components/ContainerBox';
import { getProductNameWithVariant } from 'common/utils/productUtils';
import {
	getDurationString,
	getTotalRentalDurationFromProducts,
} from 'common/utils/dateCalculations';
import { getOrderProductSkuId, getSkuIds } from 'common/modules/orders/products';
import { LoadingData } from 'services/types';
import PriceBreakdown from 'common/components/molecules/PriceBreakdown';
import { api } from 'common/frontend/api';
import { SkuItem } from 'common/modules/inventory/types';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';

const ProductDetails = () => {
	const { t } = useTranslation();
	const { localFormat } = useLocalFormat();

	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const products = useSelector(RentalViewSelectors.orderProducts);
	const orderInfo = useSelector(RentalViewSelectors.orderInfo)!;
	const lang = useSelector(LanguageSelectors.language);

	const [skuItems, setSkuItems] = useState<LoadingData<SkuItem[]>>({
		loading: true,
		data: null,
		error: null,
	});

	const groupedProducts = groupBy(
		products,
		(product) =>
			`${product.productApiId}_${product.variant?.id}_${product.startDate}_${product.endDate}_${product.pricing.total}`,
	);

	const productsTotalAmount = Object.values(groupedProducts).reduce(
		(acc, curr) => acc + curr.length,
		0,
	);

	useEffect(() => {
		const getSkuCodes = async () => {
			const skuIds = getSkuIds(products);
			try {
				const skuItems = await api().skuItems.get.whereIn('id', 'in', skuIds).get();
				setSkuItems({
					loading: false,
					data: skuItems,
					error: null,
				});
			} catch (e) {
				setSkuItems({
					loading: false,
					data: null,
					error: e.message,
				});
			}
		};

		getSkuCodes();
	}, [products, setSkuItems]);

	return (
		<ContainerBox
			sx={{
				overflowX: { sm: 'scroll', md: 'hidden' },
				p: 4,
			}}
		>
			<Typography ml={1} variant="h6">
				{t('orderSummaryDialog.productDetails.header', 'Products')}
			</Typography>
			<Table
				sx={{
					minWidth: '640px',
					'.MuiTableCell-root': {
						borderBottom: 'none',
						bgcolor: (theme) => theme.palette.background.paper,
						pl: 0,
					},
					ml: 1,
					mb: 2,
					outline: 'none',
					bgcolor: (theme) => theme.palette.background.paper,
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell>
							<Typography variant="body2" color="secondary">
								{t('orderSummaryDialog.productDetails.item', 'Item')}
							</Typography>
						</TableCell>
						<TableCell>
							<Typography variant="body2" color="secondary">
								{t('orderSummaryDialog.productDetails.duration', 'Duration')}
							</Typography>
						</TableCell>
						<TableCell>
							<Typography variant="body2" color="secondary">
								{t('orderSummaryDialog.productDetails.quantity', 'Quantity')}
							</Typography>
						</TableCell>
						<TableCell align="right">
							<Typography variant="body2" color="secondary">
								{t('orderSummaryDialog.productDetails.unitValue', {
									currency: getCurrencyObjectFromCurrencyCode(
										orderInfo.pricing.currency,
										shopCurrency,
									).displayAs,
									defaultValue: 'Unit value ({{currency}})',
								})}
							</Typography>
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{Object.values(groupedProducts).map((products) => {
						const quantity = products.length;
						const product = products[0];
						const productName = getTranslation(product.name, lang);
						const productSkuId = getOrderProductSkuId(product);
						const productSkuName = !!skuItems?.data
							? skuItems?.data.find((item) => item.id === productSkuId)?.skuCode
							: undefined;

						const isRentalProduct = product.purchaseType === 'rental';
						const isSet = !!product.set;
						const setProducts = product.setProducts;
						const orderDuration = getTotalRentalDurationFromProducts(products);
						const durationName = getDurationString(orderDuration, 'long', lang, t);
						const isFixedPriceProduct = product.startTimeSlots?.type === 'fixed';

						return (
							<TableRow sx={{ verticalAlign: 'top', borderBottom: 'none' }} key={product.id}>
								<TableCell sx={{ borderBottom: 'none' }}>
									<Stack spacing={1}>
										<Typography variant="body1">
											{getProductNameWithVariant(productName, product.variant ?? null, lang)}
										</Typography>
										{isSet ? (
											<Box>
												{setProducts.map((setProduct) => {
													const setProductSkuId = getOrderProductSkuId(setProduct);
													const skuName = !!skuItems?.data
														? skuItems.data.find((item) => item.id === setProductSkuId)?.skuCode
														: undefined;

													return (
														<>
															<Typography variant="subtitle2">
																{getProductNameWithVariant(
																	getTranslation(setProduct.name, lang),
																	setProduct.variant ?? null,
																	lang,
																)}
															</Typography>
															{!!skuName && (
																<Typography variant="subtitle2" color="secondary">
																	{`SKU: ${skuName}`}
																</Typography>
															)}
														</>
													);
												})}
											</Box>
										) : null}

										<Typography variant="body2" color="secondary">
											{!!productSkuName && !isSet && `SKU: ${productSkuName}`}
										</Typography>
									</Stack>
								</TableCell>
								<TableCell>
									{isRentalProduct ? (
										<Stack spacing={1}>
											{isFixedPriceProduct ? (
												<Typography variant="body1">{t('pricing.fixedPrice')}</Typography>
											) : (
												<>
													<Typography variant="body1">{durationName}</Typography>
													<Typography variant="body2" color="secondary">{`${
														!!product.startDate
															? localFormat(product.startDate, 'ddd D.M.YYYY HH:mm')
															: null
													} - ${
														!!product.endDate
															? localFormat(product.endDate, 'ddd D.M.YYYY HH:mm')
															: null
													}`}</Typography>
												</>
											)}
										</Stack>
									) : (
										<Typography variant="body1">{t('common:states.sold')}</Typography>
									)}
								</TableCell>
								<TableCell>{quantity}</TableCell>
								<TableCell align="right">{getPricingString(product.pricing.total)}</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
			<Divider />
			<Stack mt={2} direction="row" justifyContent="flex-end">
				<Stack sx={{ width: { xs: '100%', md: '30%' } }}>
					<Typography sx={{ fontWeight: 500 }}>
						{t('orderSummaryDialog.productDetails.orderTotal', 'Order total')}
					</Typography>
					<Stack my={1} direction="row" justifyContent="space-between">
						<Typography sx={{ fontSize: '1.4rem' }}>
							{t('orderSummaryDialog.productDetails.items', 'Items')}
						</Typography>
						<Typography sx={{ fontSize: '1.4rem' }}>{productsTotalAmount}</Typography>
					</Stack>
					<Stack>
						<PriceBreakdown
							pricing={orderInfo?.pricing}
							shopCurrency={shopCurrency}
							t={t}
							lang={lang}
							rows={['total', 'discountCodes', 'manualDiscount', 'subtotal']}
							styling={{
								verticalLinePadding: 0.5,
								totalDivider: true,
							}}
						/>
					</Stack>
				</Stack>
			</Stack>
		</ContainerBox>
	);
};

export default ProductDetails;
